/**
 * Typed wrappers for Settings "Restore defaults" + Skills "Import
 * template" — SOP §11.11 and §11.8.
 *
 * Backend (handlers/settings_restore.go + handlers/skills.go):
 *  - 7 POST /api/v1/settings/restore/* endpoints. All HR-admin gated +
 *    CSRF + audit. Response: { added_count: number }.
 *  - POST /api/v1/skills/import-template. HR-admin gated + CSRF +
 *    audit. Body: { template }. Response: { added_count, items }.
 *
 * All call sites go through the shared axios instance (lib/api/client.ts)
 * so CSRF + cookie auth + 401-refresh are handled centrally.
 */

import { api } from "./client";

export const SETTINGS_BASE = "/api/v1/settings";
export const SKILLS_BASE = "/api/v1/skills";

export interface RestoreDefaultsResponse {
  added_count: number;
}

export type RestoreCatalog =
  | "leave-types"
  | "expense-categories"
  | "asset-types"
  | "holidays"
  | "tax-codes"
  | "payment-accounts"
  | "onboarding-template";

/**
 * Restore the default rows for a catalog. Purely additive — HR edits
 * are preserved. `years` is only meaningful for the "holidays" catalog
 * (comma-separated 4-digit years; absent → server defaults to current
 * + next UTC year).
 */
export async function restoreDefaults(
  catalog: RestoreCatalog,
  opts?: { years?: number[]; state?: string },
): Promise<RestoreDefaultsResponse> {
  // Holidays only: `years`, and `state` (e.g. "TN") to add that state's
  // notified holiday list instead of the national set.
  const params = new URLSearchParams();
  if (catalog === "holidays" && opts?.years && opts.years.length > 0) {
    params.set("years", opts.years.join(","));
  }
  if (catalog === "holidays" && opts?.state) params.set("state", opts.state);
  const qs = params.toString() ? `?${params.toString().replace(/%2C/g, ",")}` : "";
  const { data } = await api.post<RestoreDefaultsResponse>(
    `${SETTINGS_BASE}/restore/${catalog}${qs}`,
  );
  return data;
}

export type SkillTemplate =
  | "engineering"
  | "sales"
  | "operations"
  | "hr";

export interface SkillCatalogItem {
  id: number;
  name: string;
  category?: string | null;
}

export interface ImportSkillTemplateResponse {
  added_count: number;
  items: SkillCatalogItem[];
}

// ---------------------------------------------------------------------------
// Settings → Modules (docs/56 U1). Tenant self-serve module ON/OFF
// switches — subtractive within the plan (down-only; the backend applies
// disabled_features LAST in ResolveFeatures, so switching off can never
// grant anything). GET = hrRole; PUT = company_admin + CSRF.
// ---------------------------------------------------------------------------

export interface ModuleState {
  feature: string;
  enabled: boolean;
}

export interface ModulesResponse {
  modules: ModuleState[];
}

/** GET /api/v1/settings/modules — entitled modules + on/off state. */
export async function getModules(signal?: AbortSignal): Promise<ModulesResponse> {
  const { data } = await api.get<ModulesResponse>(`${SETTINGS_BASE}/modules`, {
    signal,
  });
  return data;
}

/** PUT /api/v1/settings/modules — replace the OFF list. Returns fresh state. */
export async function putModules(disabled: string[]): Promise<ModulesResponse> {
  const { data } = await api.put<ModulesResponse>(`${SETTINGS_BASE}/modules`, {
    disabled,
  });
  return data;
}

/**
 * Bulk-insert one of the four hardcoded skill templates into the
 * tenant's catalog. HR-admin only. Case-insensitive dedup — the
 * response only contains newly-inserted rows.
 */
export async function importSkillTemplate(
  template: SkillTemplate,
): Promise<ImportSkillTemplateResponse> {
  const { data } = await api.post<ImportSkillTemplateResponse>(
    `${SKILLS_BASE}/import-template`,
    { template },
  );
  return data;
}

export interface SkillCatalogFull extends SkillCatalogItem {
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ListSkillsCatalogResponse {
  items: SkillCatalogFull[];
}

/** List the tenant's skill catalog. include_inactive=true for archived rows (HR-admin only). */
export async function listSkills(
  opts?: { includeInactive?: boolean },
  signal?: AbortSignal,
): Promise<ListSkillsCatalogResponse> {
  const qs = opts?.includeInactive ? "?include_inactive=true" : "";
  const { data } = await api.get<ListSkillsCatalogResponse>(
    `${SKILLS_BASE}${qs}`,
    { signal },
  );
  return data;
}

/** Create a single catalog entry. HR-admin only. */
export async function createSkill(payload: {
  name: string;
  category?: string;
}): Promise<SkillCatalogFull> {
  const { data } = await api.post<SkillCatalogFull>(SKILLS_BASE, payload);
  return data;
}

/** Patch a catalog entry (name, category, is_active). HR-admin only. */
export async function updateSkill(
  id: number,
  payload: { name?: string; category?: string; is_active?: boolean },
): Promise<SkillCatalogFull> {
  const { data } = await api.patch<SkillCatalogFull>(
    `${SKILLS_BASE}/${id}`,
    payload,
  );
  return data;
}
